// client/src/pages/IntegrationConsole.tsx
//
// Integration Console — the operating surface for the vendor-table replication
// that mirrors Practice Engine into sqldb-bmss-prod. Backed by
// api/integration-console over integration.Source / SourceRun / SourceAudit
// (team-tools migrations 001-004).
//
// WHAT THIS SCREEN IS FOR: deciding whether these mirrors are trustworthy enough
// to point an application at. That decision needs evidence over time, not a
// snapshot, which is why Scorecard exists alongside the live inventory.
//
// TWO NUMBERS THAT LOOK ALIKE AND ARE NOT:
//   Sync status  — what the sync BELIEVED happened. Every statement succeeded.
//   In sync      — whether the two sides were actually COMPARED and agreed.
// They can disagree, and that disagreement is the entire point of auditing. The UI
// keeps them in separate columns for that reason; collapsing them into one "health"
// light would hide the case we most need to see.
//
// AUDIT METHODS ARE NOT EQUIVALENT, so the method is always shown next to the
// verdict. `counts` is a weak signal: rows missing upstream and rows orphaned here
// partially cancel in a row-count delta, so a table can be badly wrong and still
// show delta 0. Only `keyset` separates "we are behind" from "we hold rows the
// source deleted". A green tick from counts and a green tick from keyset mean
// different things and the screen should never imply otherwise.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Database, RefreshCw, ShieldCheck, AlertTriangle, Clock, Play, Search,
  CheckCircle2, XCircle, MinusCircle, Settings2, History, ClipboardList, LifeBuoy,
} from 'lucide-react';

const STATUS = { good: '#0ca30c', warning: '#fab219', serious: '#ec835a', critical: '#d03b3b', idle: '#7b8794' };

type Src = {
  SourceId: number; SystemCode: string; SourceObject: string;
  TargetSchema: string; TargetTable: string; Method: string;
  IsEnabled: boolean; Priority: number;
  TargetFreshnessMinutes: number; StalenessBudgetMinutes: number;
  AgeMinutes: number | null; IsOverdue: boolean;
  KeyColumns: string | null; ChangePointerColumn: string | null;
  ChangePointerType: string | null; LastChangePointer: string | null;
  TrueUpMethod: string | null; TrueUpIntervalMinutes: number | null;
  TrueUpDaysOfWeek: string | null;
  TrueUpWindowStartHourUtc: number | null; TrueUpWindowEndHourUtc: number | null;
  IsTrueUpDue: boolean; DeleteReconcileMinutes: number | null;
  LastSyncEndUtc: string | null; LastDurationMs: number | null;
  SourceRowCount: number | null; TargetRowCount: number | null; RowsChanged: number | null;
  CountsMatch: number; LastStatus: string | null; LastError: string | null;
  ConsecutiveFailures: number;
  // From integration.MirrorFitness, which is the fallback gate itself — not a
  // recomputation of it. A non-null UnfitReason is what makes a live query read
  // Engine_BMSS instead of this mirror, so this is the verdict production acts on.
  IsQuarantined: boolean; QuarantineAfterFailures: number | null;
  UnfitReason: string | null; OrphanRows: number | null; IsTrueUpOverdue: boolean | null;
  AuditMethod: string | null; AuditIntervalMinutes: number | null;
  DeepAuditMethod: string | null; DeepAuditIntervalMinutes: number | null;
  LastAuditUtc: string | null; LastDeepAuditUtc: string | null;
  LastAuditInSync: boolean | null;
  LastAuditMethod: string | null; MissingInTarget: number | null;
  OrphanedInTarget: number | null; LastAuditRowDelta: number | null;
  SourceMaxPointer: string | null; TargetMaxPointer: string | null;
  LastAuditError: string | null; Notes: string | null;
  // Reload progress. A 20M-row truncate-and-load spans ~15 ticks over an hour and
  // a half, so without these the console would show nothing happening for that
  // whole time and the only reasonable conclusion would be that the button did
  // nothing.
  ReloadState: string; ReloadRowsStaged: number | string | null;
  ReloadSourceRowCount: number | string | null; ReloadTicks: number | null;
  ReloadStagingTable: string | null; ReloadError: string | null;
  ReloadStartedUtc: string | null; ReloadRequestedUtc: string | null;
  ReloadRequestedBy: string | null; ReloadPercent: number | null;
  ReloadElapsedMinutes: number | null; ReloadReason: string | null; InWindow: number | null;
};
type RunRow = {
  SourceRunId: number; SourceObject: string; StartedUtc: string; DurationMs: number | null;
  Status: string; RowsRead: number | null; RowsWritten: number | null;
  SourceRowCount: number | null; TargetRowCount: number | null;
  PointerFrom: string | null; PointerTo: string | null;
  TriggeredBy: string; TriggeredByUser: string | null; Error: string | null;
};
type AuditRow = {
  SourceAuditId: number; SourceObject: string; TakenUtc: string; Method: string;
  DurationMs: number | null; SourceRowCount: number | null; TargetRowCount: number | null;
  RowDelta: number | null; MissingInTarget: number | null; OrphanedInTarget: number | null;
  InSync: boolean | null; Error: string | null;
};
type ScoreRow = {
  SourceId: number; SourceObject: string; Target: string; Method: string; IsEnabled: boolean;
  Audits: number; InSyncCount: number; OutOfSyncCount: number; IndeterminateCount: number;
  FailedAudits: number; WorstRowDelta: number | null; WorstMissing: number | null;
  WorstOrphaned: number | null; KeysetAudits: number; LastAuditUtc: string | null;
};

/**
 * BIGINT columns arrive from the mssql driver as STRINGS — it will not silently
 * round past 2^53 — so every count from integration.Source must be coerced before
 * arithmetic or comparison. Skipping this turned the "rows mirrored" tile into an
 * 88-digit concatenation of every row count in the table.
 */
const num = (v: number | string | null | undefined): number => {
  if (v == null) return 0;
  const x = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(x) ? x : 0;
};

const n = (v: number | string | null | undefined) =>
  (v == null ? '—' : num(v).toLocaleString());

/** Vague on purpose: exact seconds help nobody read a freshness column. */
function age(min: number | null | undefined): string {
  if (min == null) return 'never';
  if (min < 1) return 'just now';
  if (min < 60) return `${Math.round(min)}m ago`;
  if (min < 36 * 60) return `${Math.round(min / 60)}h ago`;
  return `${Math.round(min / 1440)}d ago`;
}

/**
 * Same vague rendering as age(), from a timestamp instead of a minute count.
 *
 * The 'Z' matters. This API sends timestamps as CONVERT(varchar(19), .., 126),
 * which carries no zone, and JS parses a bare YYYY-MM-DDTHH:mm:ss as LOCAL time —
 * so without this a UTC timestamp reads five or six hours off, in the direction
 * that makes something which just happened look stale.
 */
function ago(iso: string | null | undefined): string {
  if (!iso) return 'never';
  const zoned = /[Zz]|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : `${iso}Z`;
  const ms = Date.parse(zoned);
  if (Number.isNaN(ms)) return '—';
  return age((Date.now() - ms) / 60000);
}

/**
 * Turn the common NCRONTAB shapes into plain English. Only the forms we actually
 * use are translated — a half-right cron parser that silently mislabels an
 * unusual expression would be worse than showing the raw string, since the whole
 * point of this label is telling someone what the achievable floor is.
 */
function cronHint(cron: string | null): string {
  if (!cron) return '';
  const m = /^0 \*\/(\d+) \* \* \* \*$/.exec(cron.trim());
  if (m) return ` (every ${m[1]} minute${m[1] === '1' ? '' : 's'})`;
  if (/^0 \d+ \* \* \* \*$/.test(cron.trim())) return ' (hourly)';
  return '';
}

/**
 * Explain a UTC reload window in Central time, and name the two ways it can be
 * set wrongly.
 *
 * The window is stored in UTC — the database and the Function App both work in UTC,
 * and storing a local hour would silently shift the window twice a year when the
 * clocks change. But nobody plans an overnight maintenance window by thinking in
 * UTC, and being five hours out means a full reload of a 20-million-row table
 * starting in the middle of the working day. So the Central equivalent is spelled
 * out under the control.
 *
 * CDT is -5 and CST is -6. This uses the viewer's actual current offset rather
 * than assuming either, so the hint is right on both sides of a DST change instead
 * of being right for half the year.
 */
function windowHint(startRaw: any, endRaw: any): string {
  const s = startRaw === '' || startRaw == null ? null : Number(startRaw);
  const e = endRaw === '' || endRaw == null ? null : Number(endRaw);
  if (s == null && e == null) return 'No restriction — the scheduled reload runs whenever it comes due.';
  if (s == null || e == null) return 'Set BOTH hours, or neither. One alone is ignored and the reload runs at any hour.';
  if (s === e) return 'Start and end are the same, which describes a zero-length window. Treated as no restriction rather than "never".';

  // Local offset now, in hours west of UTC.
  const offset = -new Date().getTimezoneOffset() / 60;
  const local = (h: number) => ((h + offset) % 24 + 24) % 24;
  const fmt = (h: number) => `${String(Math.floor(h)).padStart(2, '0')}:00`;
  const wraps = s > e;
  return `${fmt(s)}–${fmt(e)} UTC is ${fmt(local(s))}–${fmt(local(e))} local` +
    (wraps ? ' (crosses midnight UTC — supported).' : '.') +
    ' A scheduled reload only starts inside these hours; the reload button ignores the window.';
}

function Tile({ label, value, sub, accent }: { label: string; value: React.ReactNode; sub?: string; accent?: string }) {
  return (
    <div className="card" style={{ padding: '12px 18px', minWidth: 150, borderLeft: accent ? `3px solid ${accent}` : undefined }}>
      <div style={{ fontSize: 24, fontWeight: 700, lineHeight: 1, color: accent || 'var(--text)' }}>{value}</div>
      <div className="muted" style={{ fontSize: 12, marginTop: 5 }}>{label}</div>
      {sub && <div className="muted" style={{ fontSize: 11, marginTop: 2, opacity: .75 }}>{sub}</div>}
    </div>
  );
}

/**
 * The in-sync verdict, always rendered WITH the method that produced it.
 * A tick from `counts` and a tick from `keyset` are different claims, and showing
 * a bare tick would let the weaker one borrow the stronger one's credibility.
 */
function InSyncBadge({ inSync, method, missing, orphaned, error }: {
  inSync: boolean | null; method: string | null; missing: number | null; orphaned: number | null; error: string | null;
}) {
  if (error) {
    return <span style={{ color: STATUS.critical, display: 'inline-flex', alignItems: 'center', gap: 5 }}
      title={`Audit failed: ${error}`}><XCircle size={14} /> audit failed</span>;
  }
  if (inSync == null) {
    return <span className="muted" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
      title="No audit has answered for this object yet. Not a pass.">
      <MinusCircle size={14} /> not audited</span>;
  }
  if (inSync) {
    return <span style={{ color: STATUS.good, display: 'inline-flex', alignItems: 'center', gap: 5 }}
      title={method === 'counts'
        ? 'Row counts agreed. counts is a WEAK check — missing and orphaned rows partially cancel in a delta.'
        : `Verified by ${method}.`}>
      <CheckCircle2 size={14} /> in sync<span className="muted" style={{ fontSize: 11 }}>({method})</span></span>;
  }
  // BEHIND IS NOT DIVERGED, and showing them in the same red taught the wrong
  // lesson on day one: nine objects lit up critical when every one of them had
  // orphaned = 0 and was simply awaiting its next sync.
  //
  //   behind    the source has rows we have not pulled yet. This is the normal
  //             resting state of a mirror between syncs — a reload every 12h
  //             against a table that changes hourly is ALWAYS behind — and it
  //             makes an app answer LATE.
  //   orphaned  we hold rows the source no longer has. Nothing about waiting
  //             fixes this, and it makes an app answer WRONGLY, which is worse.
  //
  // So orphaned keeps the red and behind-only drops to amber. Both still say
  // "out of sync", because both are; the colour ranks them, it does not excuse
  // either.
  const nMissing = num(missing);
  const nOrphaned = num(orphaned);
  const bits: string[] = [];
  if (nMissing) bits.push(`${nMissing.toLocaleString()} behind`);
  if (nOrphaned) bits.push(`${nOrphaned.toLocaleString()} orphaned`);
  const divergent = nOrphaned > 0;
  return <span style={{ color: divergent ? STATUS.critical : STATUS.warning, display: 'inline-flex', alignItems: 'center', gap: 5 }}
    title={divergent
      ? 'We hold rows the source no longer has. Waiting does not fix this — it makes queries answer wrongly.'
      : 'The source has rows we have not pulled yet. Expected between syncs; concerning if it grows run over run, or if the object is past its staleness budget.'}>
    <AlertTriangle size={14} /> out of sync
    {bits.length > 0 && <span style={{ fontSize: 11 }}>({bits.join(', ')})</span>}
    <span className="muted" style={{ fontSize: 11 }}>({method})</span></span>;
}

/**
 * A reload in flight, with a bar.
 *
 * "staging" and "swapping" are shown as distinct states rather than one spinner,
 * because they mean different things to anyone watching. Staging is the long part —
 * an hour and a half for the audit table, spread over ~15 background ticks, and the
 * live table is completely untouched throughout. Swapping is the moment of
 * promotion, and it is a RENAME: metadata only, the same few milliseconds whether
 * the table holds 200 rows or 200 million. Nobody ever reads a half-loaded table.
 *
 * Progress is clamped at 99% while staging because the source keeps growing during
 * a long reload, so staged rows can legitimately pass the count captured at the
 * start. 100% would be a lie, and 103% would look like a bug.
 */
function ReloadProgress({ src }: { src: Src }) {
  const pct = src.ReloadPercent;
  const swapping = src.ReloadState === 'swapping';
  return (
    <div style={{ minWidth: 150 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, color: STATUS.warning, fontSize: 12, fontWeight: 600 }}>
        <RefreshCw size={12} />
        {swapping ? 'promoting…' : `reloading ${pct != null ? pct + '%' : ''}`}
      </div>
      <div style={{ height: 4, background: 'var(--border)', borderRadius: 2, margin: '4px 0', overflow: 'hidden' }}>
        <div style={{
          width: `${swapping ? 100 : (pct || 0)}%`, height: '100%',
          background: swapping ? STATUS.good : STATUS.warning, transition: 'width .3s',
        }} />
      </div>
      <div className="muted" style={{ fontSize: 11 }}>
        {n(src.ReloadRowsStaged)} / {n(src.ReloadSourceRowCount)} staged
      </div>
      <div className="muted" style={{ fontSize: 11 }}>
        {src.ReloadTicks ?? 0} tick{src.ReloadTicks === 1 ? '' : 's'}
        {src.ReloadElapsedMinutes != null ? ` · ${src.ReloadElapsedMinutes}m` : ''}
        {src.ReloadRequestedBy ? ` · ${src.ReloadRequestedBy}` : ''}
      </div>
      {src.ReloadError && (
        <div style={{ fontSize: 11, color: STATUS.critical, maxWidth: 240, marginTop: 2 }}>
          {/* The state is kept on failure so the next tick resumes rather than
              restarting an hour of reads, which is why this is a warning and not
              an obituary. */}
          last tick failed (will retry): {src.ReloadError}
        </div>
      )}
    </div>
  );
}

function SyncBadge({ s, failures }: { s: string | null; failures: number }) {
  if (!s) return <span className="muted">never run</span>;
  const color = s === 'ok' ? STATUS.good : s === 'drift' ? STATUS.warning : STATUS.critical;
  return (
    <span style={{ color, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
      {s}{failures > 0 && <span style={{ fontSize: 11 }}>({failures} consecutive)</span>}
    </span>
  );
}

type FallbackEvent = {
  FallbackEventId: number; QueryKey: string; SourceObject: string | null;
  Reason: string; Detail: string | null; ServeCount: number;
  WindowStartUtc: string; WindowEndUtc: string; RecordedAt: string;
};
type FallbackByQuery = {
  QueryKey: string; SourceObject: string | null; Serves: number; Windows: number;
  FirstUtc: string; LastUtc: string; Reasons: string;
};
type DrillRow = {
  DrillId: number; QueryKey: string; FallbackQueryKey: string | null; SourceObject: string | null;
  StartedAtUtc: string; DurationMs: number | null;
  MirrorRowCount: number | null; FallbackRowCount: number | null; RowCountDelta: number | null;
  MirrorMs: number | null; FallbackMs: number | null;
  ColumnsMatch: boolean | null; MissingColumns: string | null; Error: string | null;
  Ok: number; TriggeredBy: string | null; TriggeredByUser: string | null;
};

type Tab = 'inventory' | 'runs' | 'audits' | 'scorecard' | 'fallback';

export default function IntegrationConsole() {
  const [tab, setTab] = useState<Tab>('inventory');
  const [inv, setInv] = useState<Src[]>([]);
  const [runs, setRuns] = useState<RunRow[]>([]);
  const [audits, setAudits] = useState<AuditRow[]>([]);
  const [score, setScore] = useState<ScoreRow[]>([]);
  const [fbEvents, setFbEvents] = useState<FallbackEvent[]>([]);
  const [fbByQuery, setFbByQuery] = useState<FallbackByQuery[]>([]);
  const [drills, setDrills] = useState<DrillRow[]>([]);
  const [drilling, setDrilling] = useState(false);
  const [schedules, setSchedules] = useState<{ sync: string | null; syncBudgetMs: number | null; audit: string | null } | null>(null);
  const [days, setDays] = useState(14);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [editing, setEditing] = useState<Src | null>(null);

  const api = useCallback(async (qs: string, init?: RequestInit) => {
    const r = await fetch(`/api/integration-console${qs}`, {
      credentials: 'same-origin', cache: 'no-store', ...init,
    });
    const body = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(body.detail || body.error || `HTTP ${r.status}`);
    return body;
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      if (tab === 'inventory') {
        const r = await api('?mode=inventory');
        setInv(r.inventory || []);
        setSchedules(r.schedules || null);
      }
      else if (tab === 'runs') setRuns((await api('?mode=runs&hours=48')).runs || []);
      else if (tab === 'audits') setAudits((await api(`?mode=audits&days=${days}`)).audits || []);
      else if (tab === 'fallback') {
        const r = await api(`?mode=fallback&days=${days}`);
        setFbEvents(r.events || []); setFbByQuery(r.byQuery || []); setDrills(r.drills || []);
      }
      else setScore((await api(`?mode=scorecard&days=${days}`)).scorecard || []);
    } catch (e) { setErr((e as Error).message); }
    finally { setLoading(false); }
  }, [api, tab, days]);

  // Runs both sides of every mirror-backed query and compares them. Deliberately
  // something you press when nothing is wrong: failover that is only ever taken
  // during an incident is failover nobody has tested.
  const drill = useCallback(async () => {
    setDrilling(true); setErr(null); setNote(null);
    try {
      const r = await api('?action=drill', {
        method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({}),
      });
      if (r.fallback) {
        setFbEvents(r.fallback.events || []); setFbByQuery(r.fallback.byQuery || []);
        setDrills(r.fallback.drills || []);
      }
      const res = r.result || {};
      setNote(res.failed
        ? `Drill finished — ${res.failed} of ${res.drilled} fallbacks are NOT usable. See the table below.`
        : `Drill finished — all ${res.drilled || 0} fallbacks usable.`);
    } catch (e) { setErr((e as Error).message); }
    finally { setDrilling(false); }
  }, [api]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const act = useCallback(async (id: number, action: 'sync' | 'audit' | 'reload' | 'release-quarantine', method?: string) => {
    setBusyId(id); setErr(null); setNote(null);
    try {
      const r = await api(`?action=${action}`, {
        method: 'POST', headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ id, method }),
      });
      if (r.inventory) setInv(r.inventory);
      const res = r.result || {};
      if (action === 'reload') {
        const obj = (res.queued && res.queued.SourceObject) || 'the table';
        setNote(`Full reload queued for ${obj}. ` +
          'Staging starts on the next reload tick. The live table is untouched until every row is staged, ' +
          'then promoted by rename — nothing will read a half-loaded table.');
      } else if (action === 'release-quarantine') {
        setNote(`${r.released} released from quarantine — its failure count is reset and it will be ` +
          'picked up on the next cycle. If the underlying cause has not been fixed it will simply ' +
          'quarantine again after the next ten failures.');
      } else setNote(action === 'audit'
        ? `Audit complete — ${res.outOfSync || 0} out of sync of ${res.audited || 0}.`
        : `Sync complete — ${(res.tables || []).map((t: any) => `${t.sourceObject} ${t.status}`).join(', ') || 'nothing due'}.`);
    } catch (e) { setErr((e as Error).message); }
    finally { setBusyId(null); }
  }, [api]);

  const save = useCallback(async (patch: Record<string, unknown>) => {
    setErr(null); setNote(null);
    try {
      const r = await api('', {
        method: 'PATCH', headers: { 'content-type': 'application/json' }, body: JSON.stringify(patch),
      });
      if (r.inventory) setInv(r.inventory);
      setEditing(null);
      setNote('Configuration saved.');
    } catch (e) { setErr((e as Error).message); }
  }, [api]);

  const shown = useMemo(() => {
    const f = filter.trim().toLowerCase();
    if (!f) return inv;
    return inv.filter((s) => `${s.SourceObject} ${s.TargetTable} ${s.Method}`.toLowerCase().includes(f));
  }, [inv, filter]);

  const summary = useMemo(() => {
    const enabled = inv.filter((s) => s.IsEnabled);
    return {
      objects: inv.length,
      enabled: enabled.length,
      overdue: enabled.filter((s) => s.IsOverdue).length,
      failing: inv.filter((s) => s.LastStatus && s.LastStatus !== 'ok').length,
      // Counted separately from 'failing' because it is a different situation that
      // needs a different response. A failing object is being retried; a quarantined
      // one has been GIVEN UP ON and will not be retried until a person releases it.
      // Before quarantine existed, tblQuestions failed every five minutes for three
      // days and read as one more amber row.
      quarantined: inv.filter((s) => s.IsQuarantined).length,
      unfit: inv.filter((s) => s.IsEnabled && s.UnfitReason).length,
      outOfSync: inv.filter((s) => s.LastAuditInSync === false).length,
      unaudited: enabled.filter((s) => s.LastAuditInSync == null).length,
      // Number() is REQUIRED, not defensive. SourceRowCount / TargetRowCount are
      // BIGINT, and the mssql driver returns BIGINT as a STRING to avoid silently
      // losing precision past 2^53. So `a + s.TargetRowCount` concatenates instead
      // of adding, and the tile rendered an 88-digit run of every row count glued
      // together. Same family as SQL bit arriving as '0'/'1': the value looks
      // right in a log and behaves wrongly in an expression.
      rows: inv.reduce((a, s) => a + num(s.TargetRowCount), 0),
      // Behind is not the same failure as diverged, so they are counted apart.
      // Missing rows mean the mirror trails its source — expected between syncs.
      // Orphaned rows mean we hold rows the source no longer has, which is the
      // state that makes an app answer a question WRONGLY rather than late.
      orphaned: inv.filter((s) => num(s.OrphanedInTarget) > 0).length,
    };
  }, [inv]);

  return (
    <div style={{ padding: '18px 22px 60px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
        <Database size={22} />
        <h1 style={{ margin: 0, fontSize: 22 }}>Integration Console</h1>
        <button className="btn" onClick={loadAll} disabled={loading} style={{ marginLeft: 'auto' }}>
          <RefreshCw size={14} style={{ marginRight: 6 }} />{loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>
      <div className="muted" style={{ fontSize: 12.5, marginBottom: 14, maxWidth: 900 }}>
        Vendor tables replicated into <code>sqldb-bmss-prod</code>. <b>Sync status</b> is what the
        sync believed happened; <b>in sync</b> is whether the two sides were actually compared and
        agreed. They can disagree — that is what auditing is for.
      </div>

      {/* HOW TO DRIVE THIS PAGE. Written out because the control model is not
          guessable from the table: nothing here is a cron, and the field that
          actually sets an object's schedule is called "freshness". */}
      <div className="card" style={{ padding: '12px 14px', marginBottom: 14, borderLeft: `3px solid ${STATUS.idle}` }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>How scheduling works here</div>
        <div className="muted" style={{ fontSize: 12.5, lineHeight: 1.6 }}>
          There is no per-table cron. A <b>heartbeat</b> fires on a fixed interval and asks which
          objects have passed their <b>freshness target</b>; those, and only those, sync. So the
          freshness value in each row <i>is</i> that table's schedule — set it to 5 and the table
          syncs every 5 minutes.
          <br />
          <b>The heartbeat is the ceiling.</b> A target faster than the heartbeat cannot be met, and
          the table will sit permanently overdue and audit as permanently drifting.
          {schedules?.sync && (
            <>
              {' '}Currently the heartbeat is <code>{schedules.sync}</code>
              {cronHint(schedules.sync)}, so anything below that is not achievable.
            </>
          )}
          <br /><br />
          <b>Method</b> is how a table loads. <b>reload</b> replaces the whole table — simple, handles
          deletes by construction, but costs the full table every pass, which is why it suits small
          ones. <b>incremental</b> follows a rowversion and moves only what changed: tblJob_Task went
          from 338s to 2.5s. Its blind spot is deletes — a rowversion cannot express one — so every
          incremental object also gets a nightly reload to collect them, and the schema refuses an
          incremental object that has no true-up.
        </div>
      </div>

      {err && <div className="card" style={{ borderLeft: `3px solid ${STATUS.critical}`, padding: '10px 14px', marginBottom: 12, color: STATUS.critical }}>{err}</div>}
      {note && <div className="card" style={{ borderLeft: `3px solid ${STATUS.good}`, padding: '10px 14px', marginBottom: 12 }}>{note}</div>}

      {/* QUARANTINE BANNER. Deliberately above the tiles and unmissable, because the
          failure it exists for was one of visibility rather than detection:
          tblQuestions failed every five minutes for three days, was measured
          correctly every single time, and read as one more amber row in a table of
          twenty. A quarantined object is not merely unhealthy — it has been given up
          on, and it stays that way until somebody here decides otherwise. */}
      {summary.quarantined > 0 && (
        <div className="card" style={{ borderLeft: `3px solid ${STATUS.critical}`, padding: '12px 14px', marginBottom: 12 }}>
          <div style={{ fontWeight: 600, marginBottom: 6, color: STATUS.critical }}>
            {summary.quarantined} object{summary.quarantined === 1 ? '' : 's'} quarantined — not being retried
          </div>
          <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
            Each of these failed its sync too many times in a row and has been dropped from the
            rotation, which frees the concurrency slot it was consuming every cycle. Their mirrors
            are frozen at whatever they last held and every query against them falls back to
            Practice Engine. Fix the cause, then release.
          </div>
          {inv.filter((x) => x.IsQuarantined).map((x) => (
            <div key={x.SourceId} style={{ display: 'flex', alignItems: 'baseline', gap: 8, padding: '4px 0', flexWrap: 'wrap' }}>
              <code style={{ fontSize: 12 }}>{x.SourceObject}</code>
              <span className="muted" style={{ fontSize: 12 }}>
                {x.ConsecutiveFailures} consecutive failures
                {x.QuarantineAfterFailures ? ` (limit ${x.QuarantineAfterFailures})` : ''}
              </span>
              <button className="btn btn-secondary" disabled={busyId === x.SourceId}
                onClick={() => act(x.SourceId, 'release-quarantine')}
                title="Reset the failure count and return this object to the rotation">
                release
              </button>
              {x.LastError && (
                <div style={{ flexBasis: '100%', fontSize: 11, fontFamily: 'monospace', color: STATUS.critical, opacity: 0.85 }}>
                  {x.LastError}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
        <Tile label="objects" value={summary.objects} sub={`${summary.enabled} enabled`} />
        <Tile label="overdue" value={summary.overdue} accent={summary.overdue ? STATUS.warning : STATUS.good} sub="past their freshness target" />
        <Tile label="sync failing" value={summary.failing} accent={summary.failing ? STATUS.critical : STATUS.good} sub="still being retried" />
        <Tile label="quarantined" value={summary.quarantined}
          accent={summary.quarantined ? STATUS.critical : STATUS.good} sub="given up on, needs a release" />
        <Tile label="unfit to read" value={summary.unfit}
          accent={summary.unfit ? STATUS.warning : STATUS.good} sub="queries fall back to PE" />
        {/* Split, for the reason InSyncBadge explains: a mirror that is merely
            behind is waiting, and one holding orphaned rows is wrong. A single
            "out of sync" count ranks a 12-hourly reload's normal lag alongside
            actual divergence. */}
        <Tile label="behind" value={summary.outOfSync - summary.orphaned}
              accent={summary.outOfSync - summary.orphaned ? STATUS.warning : STATUS.good}
              sub="awaiting next sync" />
        <Tile label="diverged" value={summary.orphaned}
              accent={summary.orphaned ? STATUS.critical : STATUS.good}
              sub="holding rows PE deleted" />
        <Tile label="never audited" value={summary.unaudited} accent={summary.unaudited ? STATUS.warning : STATUS.good} sub="no verdict yet" />
        <Tile label="rows mirrored" value={n(summary.rows)} />
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
        {([['inventory', 'Inventory', <Settings2 size={14} />],
           ['runs', 'Run history', <History size={14} />],
           ['audits', 'Audit log', <ShieldCheck size={14} />],
           ['scorecard', 'Scorecard', <ClipboardList size={14} />],
           ['fallback', 'Fallback', <LifeBuoy size={14} />]] as const).map(([k, label, icon]) => (
          <button key={k} className={tab === k ? 'btn' : 'btn btn-secondary'} onClick={() => setTab(k as Tab)}>
            {icon}<span style={{ marginLeft: 6 }}>{label}</span>
          </button>
        ))}
        {(tab === 'audits' || tab === 'scorecard' || tab === 'fallback') && (
          <select value={days} onChange={(e) => setDays(Number(e.target.value))} style={{ marginLeft: 8 }}>
            {[1, 7, 14, 30, 60].map((d) => <option key={d} value={d}>last {d} days</option>)}
          </select>
        )}
        {tab === 'inventory' && (
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Search size={14} className="muted" />
            <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="filter tables…" style={{ width: 200 }} />
          </div>
        )}
      </div>

      {tab === 'inventory' && (
        <div className="card" style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left' }}>
                {['Source', 'Mirror', 'Method', 'Rows', 'Last sync', 'Freshness', 'In sync (method)', 'Actions'].map((h) => (
                  <th key={h} style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {shown.map((s) => (
                <tr key={s.SourceId} style={{ opacity: s.IsEnabled ? 1 : 0.5 }}>
                  <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                    <div>{s.SourceObject}</div>
                    <div className="muted" style={{ fontSize: 11 }}>{s.SystemCode}{!s.IsEnabled && ' · disabled'}</div>
                  </td>
                  <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>{s.TargetSchema}.{s.TargetTable}</td>
                  <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                    {s.Method}
                    {s.TrueUpMethod && <div className="muted" style={{ fontSize: 11 }}>+ {s.TrueUpMethod} true-up</div>}
                  </td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {n(s.TargetRowCount)}
                    {s.SourceRowCount != null && s.TargetRowCount !== s.SourceRowCount &&
                      <div style={{ fontSize: 11, color: STATUS.warning }}>src {n(s.SourceRowCount)}</div>}
                  </td>
                  <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                    {s.ReloadState !== 'idle' ? (
                      // A reload in flight replaces the sync status, because that is
                      // what is actually happening to this object right now and
                      // showing last-sync instead would be answering a question
                      // nobody is asking.
                      <ReloadProgress src={s} />
                    ) : (
                      <>
                        <SyncBadge s={s.LastStatus} failures={s.ConsecutiveFailures} />
                        {s.LastDurationMs != null && <div className="muted" style={{ fontSize: 11 }}>{(s.LastDurationMs / 1000).toFixed(1)}s</div>}
                        {s.ReloadRequestedUtc && (
                          <div style={{ fontSize: 11, color: STATUS.warning }}>reload queued</div>
                        )}
                      </>
                    )}
                  </td>
                  <td style={{ padding: '8px 10px', whiteSpace: 'nowrap', color: s.IsOverdue ? STATUS.warning : undefined }}>
                    {age(s.AgeMinutes)}
                    <div className="muted" style={{ fontSize: 11 }}>target {s.TargetFreshnessMinutes}m</div>
                  </td>
                  <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                    <InSyncBadge inSync={s.LastAuditInSync} method={s.LastAuditMethod}
                      missing={s.MissingInTarget} orphaned={s.OrphanedInTarget} error={s.LastAuditError} />
                    {s.LastAuditUtc && <div className="muted" style={{ fontSize: 11 }}>{new Date(s.LastAuditUtc + 'Z').toLocaleString()}</div>}
                  </td>
                  <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                    <button className="btn btn-secondary" disabled={busyId === s.SourceId} onClick={() => act(s.SourceId, 'sync')} title="Run this object's sync now">
                      <Play size={12} /> sync
                    </button>{' '}
                    <button className="btn btn-secondary" disabled={busyId === s.SourceId} onClick={() => act(s.SourceId, 'audit', 'keyset')} title="Full key comparison — the strong check">
                      <ShieldCheck size={12} /> audit
                    </button>{' '}
                    <button
                      className="btn btn-secondary"
                      disabled={busyId === s.SourceId || s.ReloadState !== 'idle'}
                      onClick={() => {
                        // Confirmed because it is the most expensive thing this
                        // screen can start — an hour and a half of reads against
                        // Practice Engine for the audit table — not because it is
                        // risky to the data. It is not: the live table is untouched
                        // until staging is complete.
                        const rows = num(s.SourceRowCount);
                        if (!window.confirm(
                          `Queue a FULL reload of ${s.SourceObject}?\n\n` +
                          `${rows ? rows.toLocaleString() + ' rows' : 'The whole table'} will be re-read from ` +
                          'Practice Engine into a staging table, then promoted by rename.\n\n' +
                          'Readers are unaffected throughout — the live table is not touched until every ' +
                          'row is staged. It runs across several background ticks, so you can close this page.'
                        )) return;
                        act(s.SourceId, 'reload');
                      }}
                      title={s.ReloadState !== 'idle'
                        ? `A reload is already ${s.ReloadState}`
                        : 'Full truncate-and-load: stage every row, then promote by rename. Safe at any time.'}
                    >
                      <RefreshCw size={12} /> reload
                    </button>{' '}
                    <button className="btn btn-secondary" onClick={() => setEditing(s)}><Settings2 size={12} /></button>
                  </td>
                </tr>
              ))}
              {!shown.length && <tr><td colSpan={8} className="muted" style={{ padding: 16 }}>No objects.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'runs' && (
        <div className="card" style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr style={{ textAlign: 'left' }}>
              {['Started', 'Object', 'Status', 'Read', 'Written', 'Src/Tgt', 'Duration', 'Trigger', 'Error'].map((h) =>
                <th key={h} style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)' }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r.SourceRunId}>
                  <td style={{ padding: '6px 10px', whiteSpace: 'nowrap' }}>{new Date(r.StartedUtc + 'Z').toLocaleString()}</td>
                  <td style={{ padding: '6px 10px' }}>{r.SourceObject}</td>
                  <td style={{ padding: '6px 10px', color: r.Status === 'ok' ? STATUS.good : r.Status === 'drift' ? STATUS.warning : STATUS.critical }}>{r.Status}</td>
                  <td style={{ padding: '6px 10px', textAlign: 'right' }}>{n(r.RowsRead)}</td>
                  <td style={{ padding: '6px 10px', textAlign: 'right' }}>{n(r.RowsWritten)}</td>
                  <td style={{ padding: '6px 10px', textAlign: 'right' }}>{n(r.SourceRowCount)}/{n(r.TargetRowCount)}</td>
                  <td style={{ padding: '6px 10px', textAlign: 'right' }}>{r.DurationMs == null ? '—' : `${(r.DurationMs / 1000).toFixed(1)}s`}</td>
                  <td style={{ padding: '6px 10px' }} className="muted">{r.TriggeredBy}{r.TriggeredByUser ? ` (${r.TriggeredByUser})` : ''}</td>
                  <td style={{ padding: '6px 10px', color: STATUS.critical, fontSize: 11, maxWidth: 320 }}>{r.Error || ''}</td>
                </tr>
              ))}
              {!runs.length && <tr><td colSpan={9} className="muted" style={{ padding: 16 }}>No runs in the last 48 hours.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'audits' && (
        <div className="card" style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr style={{ textAlign: 'left' }}>
              {['Taken', 'Object', 'Method', 'Verdict', 'Src/Tgt', 'Delta', 'Behind', 'Orphaned', 'Duration'].map((h) =>
                <th key={h} style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)' }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {audits.map((a) => (
                <tr key={a.SourceAuditId}>
                  <td style={{ padding: '6px 10px', whiteSpace: 'nowrap' }}>{new Date(a.TakenUtc + 'Z').toLocaleString()}</td>
                  <td style={{ padding: '6px 10px' }}>{a.SourceObject}</td>
                  <td style={{ padding: '6px 10px' }}>{a.Method}</td>
                  <td style={{ padding: '6px 10px' }}>
                    <InSyncBadge inSync={a.InSync} method={a.Method} missing={a.MissingInTarget} orphaned={a.OrphanedInTarget} error={a.Error} />
                  </td>
                  <td style={{ padding: '6px 10px', textAlign: 'right' }}>{n(a.SourceRowCount)}/{n(a.TargetRowCount)}</td>
                  <td style={{ padding: '6px 10px', textAlign: 'right', color: a.RowDelta ? STATUS.warning : undefined }}>{n(a.RowDelta)}</td>
                  <td style={{ padding: '6px 10px', textAlign: 'right' }}>{n(a.MissingInTarget)}</td>
                  <td style={{ padding: '6px 10px', textAlign: 'right' }}>{n(a.OrphanedInTarget)}</td>
                  <td style={{ padding: '6px 10px', textAlign: 'right' }}>{a.DurationMs == null ? '—' : `${(a.DurationMs / 1000).toFixed(1)}s`}</td>
                </tr>
              ))}
              {!audits.length && <tr><td colSpan={9} className="muted" style={{ padding: 16 }}>
                No audits in this window. auditTimer runs hourly at :20 UTC.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'scorecard' && (
        <div className="card" style={{ overflowX: 'auto' }}>
          <div className="muted" style={{ fontSize: 12, padding: '10px 12px 0' }}>
            How clean each mirror has actually been over the window — the evidence for deciding
            whether an app can read it. <b>Indeterminate</b> is counted separately: an audit that
            could not answer is not a pass. <b>Keyset</b> counts how many of those checks were the
            strong comparison rather than row counts.
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginTop: 8 }}>
            <thead><tr style={{ textAlign: 'left' }}>
              {['Object', 'Mirror', 'Audits', 'In sync', 'Out of sync', 'Indeterminate', 'Failed', 'Keyset', 'Worst behind', 'Worst orphaned', 'Rate'].map((h) =>
                <th key={h} style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)' }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {score.map((s) => {
                const judged = s.InSyncCount + s.OutOfSyncCount;
                const rate = judged ? (s.InSyncCount / judged) * 100 : null;
                return (
                  <tr key={s.SourceId} style={{ opacity: s.IsEnabled ? 1 : 0.5 }}>
                    <td style={{ padding: '6px 10px' }}>{s.SourceObject}</td>
                    <td style={{ padding: '6px 10px' }} className="muted">{s.Target}</td>
                    <td style={{ padding: '6px 10px', textAlign: 'right' }}>{s.Audits}</td>
                    <td style={{ padding: '6px 10px', textAlign: 'right', color: STATUS.good }}>{s.InSyncCount}</td>
                    <td style={{ padding: '6px 10px', textAlign: 'right', color: s.OutOfSyncCount ? STATUS.critical : undefined }}>{s.OutOfSyncCount}</td>
                    <td style={{ padding: '6px 10px', textAlign: 'right' }} className="muted">{s.IndeterminateCount}</td>
                    <td style={{ padding: '6px 10px', textAlign: 'right', color: s.FailedAudits ? STATUS.critical : undefined }}>{s.FailedAudits}</td>
                    <td style={{ padding: '6px 10px', textAlign: 'right' }}>{s.KeysetAudits}</td>
                    <td style={{ padding: '6px 10px', textAlign: 'right' }}>{n(s.WorstMissing)}</td>
                    <td style={{ padding: '6px 10px', textAlign: 'right' }}>{n(s.WorstOrphaned)}</td>
                    <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 600,
                                color: rate == null ? undefined : rate === 100 ? STATUS.good : rate >= 95 ? STATUS.warning : STATUS.critical }}>
                      {rate == null ? '—' : `${rate.toFixed(1)}%`}
                    </td>
                  </tr>
                );
              })}
              {!score.length && <tr><td colSpan={11} className="muted" style={{ padding: 16 }}>No audit data in this window yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'fallback' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="card" style={{ padding: '12px 14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <LifeBuoy size={16} />
              <b style={{ fontSize: 14 }}>Mirror → Practice Engine fallback</b>
              <button className="btn btn-secondary" style={{ marginLeft: 'auto' }} onClick={drill} disabled={drilling}>
                <Play size={14} /><span style={{ marginLeft: 6 }}>{drilling ? 'Drilling…' : 'Run drill'}</span>
              </button>
            </div>
            <div className="muted" style={{ fontSize: 12, marginTop: 8, lineHeight: 1.55 }}>
              When a mirror is unfit — disabled, never loaded, sync failing, past its staleness budget,
              row counts disagreeing, or an audit finding drift — the gateway serves the query from
              Practice Engine instead. The user sees normal data and no error. This tab is the only
              place that fact is visible.
              <br /><br />
              <b>A drill runs both sides and compares them, while nothing is wrong.</b> That is the point:
              failover only ever taken during an incident is failover nobody has tested. It checks that
              the PE twin still returns the <b>same columns</b> — a twin returning correct data under
              different names renders as empty cells, so the failure looks like missing data rather than
              broken failover — and that the twin is kept warm, because a cold PE read on this query has
              measured 79.6s, which the caller would time out on.
            </div>
          </div>

          <div className="card" style={{ overflowX: 'auto' }}>
            <div style={{ fontWeight: 600, fontSize: 13, padding: '10px 12px 0' }}>Drill history</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginTop: 8 }}>
              <thead><tr style={{ textAlign: 'left' }}>
                {['When', 'Query', 'Twin', 'Result', 'Rows mirror / PE', 'Delta', 'Mirror ms', 'PE ms', 'By'].map((h) =>
                  <th key={h} style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)' }}>{h}</th>)}
              </tr></thead>
              <tbody>
                {drills.map((d) => (
                  <tr key={d.DrillId}>
                    <td style={{ padding: '6px 10px', whiteSpace: 'nowrap' }} className="muted">{ago(d.StartedAtUtc)}</td>
                    <td style={{ padding: '6px 10px' }}>{d.QueryKey}</td>
                    <td style={{ padding: '6px 10px' }} className="muted">{d.FallbackQueryKey || '—'}</td>
                    <td style={{ padding: '6px 10px' }}>
                      {d.Ok ? (
                        <span style={{ color: STATUS.good, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <CheckCircle2 size={13} /> usable
                        </span>
                      ) : (
                        <span style={{ color: STATUS.critical, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <XCircle size={13} /> {d.Error ? 'failed' : d.ColumnsMatch === null ? 'no rows to compare' : 'columns differ'}
                        </span>
                      )}
                      {(d.Error || d.MissingColumns) && (
                        <div className="muted" style={{ fontSize: 11, marginTop: 2, maxWidth: 420 }}>
                          {d.Error || d.MissingColumns}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '6px 10px', textAlign: 'right' }}>
                      {n(d.MirrorRowCount)} / {n(d.FallbackRowCount)}
                    </td>
                    {/* A small positive delta is the expected signature of a mirror that
                        trails its source, so it is shown plainly rather than flagged.
                        What matters is the trend across drills. */}
                    <td style={{ padding: '6px 10px', textAlign: 'right' }} className="muted">
                      {d.RowCountDelta == null ? '—' : d.RowCountDelta > 0 ? `+${d.RowCountDelta}` : d.RowCountDelta}
                    </td>
                    <td style={{ padding: '6px 10px', textAlign: 'right' }} className="muted">{n(d.MirrorMs)}</td>
                    <td style={{ padding: '6px 10px', textAlign: 'right',
                                 color: (d.FallbackMs || 0) > 5000 ? STATUS.warning : undefined }}>{n(d.FallbackMs)}</td>
                    <td style={{ padding: '6px 10px' }} className="muted">{d.TriggeredByUser || d.TriggeredBy || '—'}</td>
                  </tr>
                ))}
                {!drills.length && (
                  <tr><td colSpan={9} className="muted" style={{ padding: 16 }}>
                    Never drilled. The fallback path has not been exercised, so whether it works is
                    currently unknown — press <b>Run drill</b> above.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="card" style={{ overflowX: 'auto' }}>
            <div style={{ fontWeight: 600, fontSize: 13, padding: '10px 12px 0' }}>
              Fallback serves — last {days} days
            </div>
            <div className="muted" style={{ fontSize: 12, padding: '4px 12px 0' }}>
              Counted as <b>serves</b>, not rows in a table: the gateway aggregates repeated fallbacks
              into windows, so one row can represent many requests.
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginTop: 8 }}>
              <thead><tr style={{ textAlign: 'left' }}>
                {['Query', 'Mirror', 'Serves', 'Windows', 'Reasons', 'First', 'Last'].map((h) =>
                  <th key={h} style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)' }}>{h}</th>)}
              </tr></thead>
              <tbody>
                {fbByQuery.map((f) => (
                  <tr key={f.QueryKey + (f.SourceObject || '')}>
                    <td style={{ padding: '6px 10px' }}>{f.QueryKey}</td>
                    <td style={{ padding: '6px 10px' }} className="muted">{f.SourceObject || '—'}</td>
                    <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 600, color: STATUS.warning }}>{n(f.Serves)}</td>
                    <td style={{ padding: '6px 10px', textAlign: 'right' }} className="muted">{n(f.Windows)}</td>
                    <td style={{ padding: '6px 10px' }}>{f.Reasons}</td>
                    <td style={{ padding: '6px 10px', whiteSpace: 'nowrap' }} className="muted">{ago(f.FirstUtc)}</td>
                    <td style={{ padding: '6px 10px', whiteSpace: 'nowrap' }} className="muted">{ago(f.LastUtc)}</td>
                  </tr>
                ))}
                {!fbByQuery.length && (
                  <tr><td colSpan={7} className="muted" style={{ padding: 16 }}>
                    No fallbacks in this window. That means either the mirrors held, or nothing is
                    reading them yet — the drill history above distinguishes the two.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>

          {fbEvents.length > 0 && (
            <div className="card" style={{ overflowX: 'auto' }}>
              <div style={{ fontWeight: 600, fontSize: 13, padding: '10px 12px 0' }}>Individual windows</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginTop: 8 }}>
                <thead><tr style={{ textAlign: 'left' }}>
                  {['Recorded', 'Query', 'Mirror', 'Reason', 'Serves', 'Detail'].map((h) =>
                    <th key={h} style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)' }}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {fbEvents.map((e) => (
                    <tr key={e.FallbackEventId}>
                      <td style={{ padding: '6px 10px', whiteSpace: 'nowrap' }} className="muted">{ago(e.RecordedAt)}</td>
                      <td style={{ padding: '6px 10px' }}>{e.QueryKey}</td>
                      <td style={{ padding: '6px 10px' }} className="muted">{e.SourceObject || '—'}</td>
                      <td style={{ padding: '6px 10px' }}>{e.Reason}</td>
                      <td style={{ padding: '6px 10px', textAlign: 'right' }}>{n(e.ServeCount)}</td>
                      <td style={{ padding: '6px 10px', maxWidth: 380 }} className="muted">{e.Detail || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {editing && <EditPanel src={editing} onCancel={() => setEditing(null)} onSave={save} />}
    </div>
  );
}

/**
 * Config editor. Only fields that are safe to change at runtime appear here —
 * SourceObject, TargetTable and KeyColumns define identity and correctness and
 * live in migrations, where they get reviewed.
 *
 * Validation is left to the database. integration.Source carries CHECK constraints
 * (an incremental object must declare a delete strategy, a staleness budget cannot
 * be below its freshness target, and so on) and the API surfaces the rejection
 * verbatim. Re-implementing those rules here would give two places to disagree.
 */
function EditPanel({ src, onCancel, onSave }: {
  src: Src; onCancel: () => void; onSave: (patch: Record<string, unknown>) => void;
}) {
  const [f, setF] = useState<Record<string, any>>({
    IsEnabled: src.IsEnabled ? 1 : 0,
    Method: src.Method,
    TargetFreshnessMinutes: src.TargetFreshnessMinutes,
    StalenessBudgetMinutes: src.StalenessBudgetMinutes,
    AuditMethod: src.AuditMethod ?? '',
    AuditIntervalMinutes: src.AuditIntervalMinutes ?? '',
    DeepAuditMethod: src.DeepAuditMethod ?? '',
    DeepAuditIntervalMinutes: src.DeepAuditIntervalMinutes ?? '',
    TrueUpMethod: src.TrueUpMethod ?? '',
    TrueUpIntervalMinutes: src.TrueUpIntervalMinutes ?? '',
    TrueUpDaysOfWeek: src.TrueUpDaysOfWeek ?? '',
    TrueUpWindowStartHourUtc: src.TrueUpWindowStartHourUtc ?? '',
    TrueUpWindowEndHourUtc: src.TrueUpWindowEndHourUtc ?? '',
    Priority: src.Priority,
  });
  const set = (k: string, v: any) => setF((p) => ({ ...p, [k]: v }));
  const row = (label: string, node: React.ReactNode, hint?: string) => (
    <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: 10, alignItems: 'center', marginBottom: 8 }}>
      <label className="muted" style={{ fontSize: 12.5 }}>{label}</label>
      <div>{node}{hint && <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>{hint}</div>}</div>
    </div>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}
         onClick={onCancel}>
      <div className="card" style={{ width: 620, maxHeight: '86vh', overflowY: 'auto', padding: 20 }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>{src.SourceObject} → {src.TargetSchema}.{src.TargetTable}</h3>
        <div className="muted" style={{ fontSize: 12, marginBottom: 14 }}>
          Keys <code>{src.KeyColumns || '—'}</code> · pointer <code>{src.ChangePointerColumn || '—'}</code>
          {src.LastChangePointer && <> · watermark <code>{src.LastChangePointer}</code></>}
          <div style={{ marginTop: 4 }}>Source, target and keys are set in migrations, not here.</div>
        </div>

        {row('Enabled', <input type="checkbox" checked={!!f.IsEnabled} onChange={(e) => set('IsEnabled', e.target.checked ? 1 : 0)} />,
          'Disabled objects are skipped by the timer but stay visible.')}
        {row('Method', <select value={f.Method} onChange={(e) => set('Method', e.target.value)}>
          {['reload', 'projection', 'incremental', 'upsert'].map((m) => <option key={m}>{m}</option>)}</select>,
          'incremental/upsert require a change pointer, keys, and a delete strategy — the database enforces this.')}
        {row('Freshness target (min)', <input type="number" value={f.TargetFreshnessMinutes} onChange={(e) => set('TargetFreshnessMinutes', e.target.value)} />,
          'How stale this may get before the timer picks it up. Raise it to slow the integration down.')}
        {row('Staleness budget (min)', <input type="number" value={f.StalenessBudgetMinutes} onChange={(e) => set('StalenessBudgetMinutes', e.target.value)} />,
          'Past this the gateway refuses to serve rather than return data of unknown age. Must be ≥ the target.')}
        {row('True-up method', <select value={f.TrueUpMethod} onChange={(e) => set('TrueUpMethod', e.target.value)}>
          <option value="">none</option><option value="reload">reload</option><option value="projection">projection</option></select>,
          'The nightly full restatement that corrects deletes. Required for incremental unless a delete reconcile is set.')}
        {row('True-up interval (min)', <input type="number" value={f.TrueUpIntervalMinutes} onChange={(e) => set('TrueUpIntervalMinutes', e.target.value)} />, '1440 = daily.')}
        {/* WHICH DAYS the scheduled reload may run.
            Needed because the interval alone cannot pin a weekday: a 7-day interval
            fires seven days after the LAST run, so the day drifts every time a run
            is delayed, and a "weekend" reload ends up midweek within a month. */}
        {row('Reload days',
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => {
              const set_ = String(f.TrueUpDaysOfWeek || '').split(',').map((x) => x.trim()).filter(Boolean);
              const on = set_.some((x) => x.toLowerCase() === d.toLowerCase());
              return (
                <label key={d} style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                  <input type="checkbox" checked={on} onChange={(e) => {
                    const next = e.target.checked
                      ? [...set_.filter((x) => x.toLowerCase() !== d.toLowerCase()), d]
                      : set_.filter((x) => x.toLowerCase() !== d.toLowerCase());
                    // Canonical weekday order, so the stored value reads naturally
                    // rather than in whatever order the boxes were clicked.
                    const order = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
                    next.sort((a, b) => order.indexOf(a.toLowerCase()) - order.indexOf(b.toLowerCase()));
                    set('TrueUpDaysOfWeek', next.join(','));
                  }} />
                  {d}
                </label>
              );
            })}
          </div>,
          f.TrueUpDaysOfWeek
            ? `Scheduled reload runs only on ${f.TrueUpDaysOfWeek}, inside the hours below.`
            : 'None selected = any day. The schema rejects an unrecognised day name rather than silently never reloading.')}
        {/* THE HOURS THE SCHEDULED RELOAD IS ALLOWED TO RUN.
            Stored in UTC because the database and the Function App both work in UTC
            and a stored local hour would silently shift twice a year. Shown in
            Central as well, because nobody schedules their overnight window by
            thinking in UTC, and getting it wrong by five hours means a full reload
            of a 20M-row table starting in the middle of the working day. */}
        {row('Reload window (UTC hours)',
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <select value={f.TrueUpWindowStartHourUtc} onChange={(e) => set('TrueUpWindowStartHourUtc', e.target.value)}>
              <option value="">any</option>
              {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>)}
            </select>
            <span className="muted">to</span>
            <select value={f.TrueUpWindowEndHourUtc} onChange={(e) => set('TrueUpWindowEndHourUtc', e.target.value)}>
              <option value="">any</option>
              {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>)}
            </select>
          </div>,
          windowHint(f.TrueUpWindowStartHourUtc, f.TrueUpWindowEndHourUtc))}
        {row('Audit method', <select value={f.AuditMethod} onChange={(e) => set('AuditMethod', e.target.value)}>
          <option value="">none</option>{['counts', 'pointer', 'keyset'].map((m) => <option key={m}>{m}</option>)}</select>,
          'The cheap, frequent check. counts is weak — behind and orphaned rows partially cancel.')}
        {row('Audit interval (min)', <input type="number" value={f.AuditIntervalMinutes} onChange={(e) => set('AuditIntervalMinutes', e.target.value)} />)}
        {row('Deep audit method', <select value={f.DeepAuditMethod} onChange={(e) => set('DeepAuditMethod', e.target.value)}>
          <option value="">none</option>{['pointer', 'keyset'].map((m) => <option key={m}>{m}</option>)}</select>,
          'keyset is the only check that finds rows the source deleted.')}
        {row('Deep audit interval (min)', <input type="number" value={f.DeepAuditIntervalMinutes} onChange={(e) => set('DeepAuditIntervalMinutes', e.target.value)} />)}
        {row('Priority', <input type="number" value={f.Priority} onChange={(e) => set('Priority', e.target.value)} />, 'Lower runs first when several are due.')}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button className="btn btn-secondary" onClick={onCancel}>Cancel</button>
          <button className="btn" onClick={() => onSave({ id: src.SourceId, ...f })}>Save</button>
        </div>
      </div>
    </div>
  );
}
